import { HsDefaultMaterial, HsDefaultShader, HsShaderObjectType } from '@sibe/hyperscene';

// The current engine has no per-vertex color attribute for meshes, so the normal attribute slot carries the
// colors and the fragment stage derives flat face normals from screen-space derivatives instead.
const VERTEX_CODE: string = `#version 300 es
precision highp float;

in vec3 a_positions;
in vec3 a_normals;

uniform mat4 u_projectionMatrix;
uniform mat4 u_relativeMatrix;

out vec3 v_relativePosition;
out vec3 v_color;
out float v_fragDepth;

void main() {
  vec4 relativePosition = u_relativeMatrix * vec4(a_positions, 1.0);
  v_relativePosition = relativePosition.xyz;
  gl_Position = u_projectionMatrix * relativePosition;
  v_fragDepth = 1.0 + gl_Position.w;
  v_color = a_normals;
}
`;

const FRAGMENT_CODE: string = `#version 300 es
precision highp float;

uniform float u_logDepthBufFC;

in vec3 v_relativePosition;
in vec3 v_color;
in float v_fragDepth;

layout(location = 0) out vec4 fragmentColor;

void main() {
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  vec3 normal = normalize(cross(dFdx(v_relativePosition), dFdy(v_relativePosition)));
  // Light the side that faces the camera, whatever the triangle winding.
  if (dot(normal, v_relativePosition) > 0.0) {
    normal = -normal;
  }
  float diffuse = max(dot(normal, normalize(vec3(0.5, 1.0, 0.3))), 0.0);
  fragmentColor = vec4(v_color * (0.45 + 0.55 * diffuse), 1.0);
}
`;

/**
 * A lit material that colors every vertex on its own, the nearest current-engine equivalent of the legacy
 * `CompositeMaterial`. Pass the vertex colors in place of normals when building the geometry.
 */
export function makeVertexColorMaterial(gl: WebGL2RenderingContext): HsDefaultMaterial<HsDefaultShader> {
  const shader: HsDefaultShader = new HsDefaultShader(HsShaderObjectType.single);
  shader.makeVertexCode = (): string => VERTEX_CODE;
  shader.makeFragmentCode = (): string => FRAGMENT_CODE;
  return new HsDefaultMaterial(gl, shader);
}
